import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, ImageIcon, Share2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMessage } from "../../lib/api";
import { certificatePng } from "./MilestoneCertificate";

// Colorful 1080x1080 Monthly Recap Poster: one month of family practice wins,
// ready to print or share. Bright daytime palette (the certificate stays dark).
const PosterSVG = ({ svgRef, monthLabel, stats }) => {
  const tiles = [
    [stats.minutes, "practice minutes"],
    [stats.stickers, "stickers earned"],
    [stats.boss_wins, `boss win${stats.boss_wins === 1 ? "" : "s"}`],
    [stats.rally_wins, `rally win${stats.rally_wins === 1 ? "" : "s"}`],
  ];
  const kids = stats.per_child.slice(0, 4);
  return (
    <svg ref={svgRef} viewBox="0 0 1080 1080" xmlns="http://www.w3.org/2000/svg"
      className="w-full rounded-xl border border-border" role="img"
      aria-label={`Monthly recap poster for ${monthLabel}`} data-testid="poster-preview">
      <defs>
        <linearGradient id="pbg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FEF3C7" />
          <stop offset="55%" stopColor="#FDE68A" />
          <stop offset="100%" stopColor="#FCA5A5" />
        </linearGradient>
        <linearGradient id="psun" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>
      </defs>
      <rect width="1080" height="1080" fill="url(#pbg)" />
      <circle cx="900" cy="150" r="90" fill="url(#psun)" opacity="0.9" />
      {[[120, 200], [220, 120], [960, 380], [90, 620], [1000, 760]].map(([cx, cy], i) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={i % 2 ? 10 : 14} fill="#FFFFFF" opacity="0.55" />
      ))}
      <text x="540" y="130" textAnchor="middle" fill="#92400E"
        fontFamily="Arial, sans-serif" fontSize="34" fontWeight="bold" letterSpacing="8">
        OUR FAMILY PRACTICE POSTER
      </text>
      <text x="540" y="230" textAnchor="middle" fill="#7C2D12"
        fontFamily="Georgia, serif" fontSize="78" fontWeight="bold">
        {monthLabel}
      </text>
      {tiles.map(([n, label], i) => {
        const x = 110 + (i % 2) * 440;
        const y = 300 + Math.floor(i / 2) * 250;
        return (
          <g key={label}>
            <rect x={x} y={y} width="420" height="210" rx="28" fill="#FFFFFF" opacity="0.92" />
            <text x={x + 210} y={y + 105} textAnchor="middle" fill="#B45309"
              fontFamily="Arial, sans-serif" fontSize="84" fontWeight="bold">{n}</text>
            <text x={x + 210} y={y + 165} textAnchor="middle" fill="#78716C"
              fontFamily="Arial, sans-serif" fontSize="30">{label}</text>
          </g>
        );
      })}
      {kids.map((c, i) => {
        const w = 980 / kids.length;
        const x = 50 + i * w + w / 2;
        return (
          <text key={c.name + i} x={x} y="880" textAnchor="middle" fill="#7C2D12"
            fontFamily="Arial, sans-serif" fontSize="36" fontWeight="bold">
            {/* single text node: Chromium drops mixed JSX text-node children in SVG */}
            {`${c.name} · ${c.minutes} min`}
          </text>
        );
      })}
      <text x="540" y="960" textAnchor="middle" fill="#92400E"
        fontFamily="Arial, sans-serif" fontSize="30" fontWeight="bold">
        Every minute together counts!
      </text>
      <text x="540" y="1015" textAnchor="middle" fill="#A16207"
        fontFamily="Arial, sans-serif" fontSize="20">
        M Speech, Language &amp; Math Helper
      </text>
    </svg>
  );
};

const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i += 1) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({
      value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`,
      label: m.toLocaleString("en", { month: "long", year: "numeric" }),
    });
  }
  return out;
};

export const MonthlyPoster = () => {
  const [months] = useState(monthOptions);
  const [month, setMonth] = useState(months[0].value);
  const [stats, setStats] = useState(null); // null | "loading" | data
  const [wall, setWall] = useState([]); // Poster Wall: last 6 months at a glance
  const [busy, setBusy] = useState(false);
  const svgRef = useRef(null);
  const wallFetched = useRef(false);

  useEffect(() => {
    if (wallFetched.current) return;
    wallFetched.current = true;
    api.get("/recap/poster-wall", { params: { months: 6 } })
      .then(({ data }) => setWall(data.months || []))
      .catch(() => setWall([]));
  }, []);

  const open = async (m) => {
    setMonth(m);
    setStats("loading");
    try {
      const { data } = await api.get("/recap/poster", { params: { month: m } });
      setStats(data);
    } catch (e) {
      setStats(null);
      toast.error(errMessage(e));
    }
  };

  const exportPng = async (share) => {
    setBusy(true);
    try {
      const blob = await certificatePng(svgRef.current);
      const file = new File([blob], `family-poster-${month}.png`, { type: "image/png" });
      if (share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Our family practice poster" });
        toast.success("Poster shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `family-poster-${month}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Poster downloaded — print it or share it!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the poster");
    }
    setBusy(false);
  };

  const label = months.find((m) => m.value === month)?.label || month;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 flex flex-wrap items-center justify-between gap-4" data-testid="monthly-poster-card">
      <div className="flex items-center gap-3">
        <ImageIcon className="h-5 w-5 text-secondary" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">Monthly recap poster</p>
          <p className="text-xs text-muted-foreground">Turn a month of practice wins into a colorful poster to print or share.</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <select value={month} onChange={(e) => setMonth(e.target.value)}
          className="h-8 rounded-lg border border-border bg-card px-2 text-xs"
          aria-label="Poster month" data-testid="poster-month-select">
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <Button variant="outline" size="sm" className="rounded-full"
          onClick={() => open(month)} data-testid="poster-create-button">
          Create poster
        </Button>
      </div>
      {wall.length > 0 && (
        <div className="w-full flex flex-wrap gap-2 pt-1" data-testid="poster-wall">
          {wall.map((m) => {
            const label = new Date(`${m.month}-01T00:00:00`).toLocaleString("en", { month: "short", year: "2-digit" });
            return (
              <button key={m.month} type="button"
                onClick={() => open(m.month)}
                className="rounded-xl border border-border bg-muted/50 px-3 py-1.5 text-left hover:bg-muted transition-colors"
                aria-label={`Open ${m.month} poster`}
                data-testid={`poster-wall-${m.month}`}>
                <span className="block text-xs font-bold">{label}</span>
                <span className="block text-[10px] text-muted-foreground">{`${m.minutes} min · ${m.rally_wins} rally`}</span>
              </button>
            );
          })}
        </div>
      )}
      <Dialog open={stats !== null} onOpenChange={(o) => !o && setStats(null)}>
        <DialogContent className="max-w-xl" data-testid="poster-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{label} — family poster</DialogTitle>
          </DialogHeader>
          {stats === "loading" || !stats ? (
            <p className="text-sm text-muted-foreground" data-testid="poster-loading">Painting your poster…</p>
          ) : (
            <div className="space-y-3">
              <PosterSVG svgRef={svgRef} monthLabel={label} stats={stats} />
              <div className="flex gap-2">
                <Button className="rounded-full gap-1.5" disabled={busy}
                  onClick={() => exportPng(true)} data-testid="poster-share-button">
                  <Share2 className="h-4 w-4" aria-hidden="true" /> Share
                </Button>
                <Button variant="outline" className="rounded-full gap-1.5" disabled={busy}
                  onClick={() => exportPng(false)} data-testid="poster-download-button">
                  <Download className="h-4 w-4" aria-hidden="true" /> Download PNG
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
